import CircleCheckIcon from '@persona-id/design-tokens/icons/CircleCheckIcon';
import DatabaseIcon from '@persona-id/design-tokens/icons/DatabaseIcon';
import ListIcon from '@persona-id/design-tokens/icons/ListIcon';
import SettingsIcon from '@persona-id/design-tokens/icons/SettingsIcon';
import { keyBy } from 'lodash/fp';
import {
  lazy,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { MINIMUM_BOTTOM_PANEL_HEIGHT } from 'dashboard/apps/InquiryTemplateEditor/BottomPanel/BottomPanel';
import CompleteStepPanel from 'dashboard/apps/InquiryTemplateEditor/CompleteStepPanel';
import FieldSchemaEditorCardV2 from 'dashboard/apps/InquiryTemplateEditor/FieldSchemaEditorCard/FieldSchemaEditorCardV2';
import FormSectionGeneralConfig from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionGeneralConfig';
import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import {
  buildFieldReferenceMap,
  FieldReferenceMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateConfigEditor/utils';
import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import {
  CombinedStepForTree,
  LinkedWorkflowStep,
  PreviewPageMap,
  StepPreviewMap,
  TemplateStepPreview,
  TreeNode,
  VerificationTemplateVersionMap,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  buildConnectedSteps,
  buildScreenTreeAndGraph,
  deriveCombinedStepScreenName,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import InquiryTemplateEditorToolbar, {
  TOOLBAR_HEIGHT,
  TOOLBAR_TRANSITION,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import { EDIT_CONDITIONAL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/constants';
import {
  AddStepConfig,
  DeleteStepConfig,
  FlowTreeFormValues,
  NewStep,
  ScreenFlowStepType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import {
  copiedStepSanitized,
  findStepInTree,
  getConditionalStepForToken,
  getConnectedSchemaMaps,
  getDedupedFieldSchema,
  getNewModuleConfig,
  getRootNodeToken,
  getWorkflowVersionMapUpdated,
  isScreenStep,
  stepsWithDeletedScreenHandled,
  stepsWorkflowsPreviewsWithDeletedScreenNode,
  stepsWorkflowsPreviewsWithDeletedWorkflowNode,
  stepsWorkflowsPreviewsWithInserted,
  stepsWorkflowsWithInsertedActionNode,
  stepsWorkflowsWithInsertedBranchingNode,
  stepsWorkflowsWithInsertedConnectorNode,
  stepsWorkflowsWithInsertedModule,
  vtmplVersionMapWithInserted,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import InquiryTemplateTree from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree';
import {
  InquiryTemplateTreeProvider,
  useInquiryTemplateTreeStore,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/InquiryTemplateTreeProvider';
import TreeNavigationNullState from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/TreeNavigationNullState';
import ScreenFlowAddModuleModal from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowAddModuleModal';
import WorkflowStepPanel from 'dashboard/apps/InquiryTemplateEditor/WorkflowStepPanel';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavAndPanelsOverlay, { NavItem } from 'dashboard/components-lib/NavAndPanelsOverlay';
import { LEFT_NAV_WIDTH } from 'dashboard/components-lib/NavAndPanelsOverlay/constants';
import PanZoomBox from 'dashboard/components-lib/PanZoomBox';
import VersionsSection from 'dashboard/components-lib/VersionsSection';
import { inquiryTemplateVersionMetadata } from 'dashboard/components-lib/VersionsSection/utils';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { createMockVerificationTemplateForType } from 'dashboard/lib/stubs/verification-template';
import { TransformationMatrix2D } from 'dashboard/lib/utils/cssTransformationMatrix';
import {
  InquiryTemplateModule,
  InquiryTemplateModuleOption,
  ModuleOptionKey,
  ModulePreviewMap,
} from 'dashboard/models/inquiry-template-module';
import {
  InquiryTemplateVersion,
  PartialInquiryTemplateVersion,
} from 'dashboard/models/inquiry-template-version';
import { VTMPLV_TEMPLATE_TYPE_MAP } from 'dashboard/models/verification-template-version';
import { WorkflowConfigError } from 'dashboard/models/workflow';
import { WorkflowStepType } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { Z_INDICES } from 'dashboard/styles/variables';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import { assertNever } from 'lib/utils/types';
import { removeLastPath } from 'lib/utils/url';

const NAV_PANEL_WIDTH = 250;
const FLE2_ZOOM_TO_FIT_PADDING = {
  top: TOOLBAR_HEIGHT + 16,
  bottom: 16,
  left: NAV_PANEL_WIDTH + 16,
};
const FLE3_ZOOM_TO_FIT_PADDING = {
  // TODO (fawn): Remove TOOLBAR_HEIGHT from this top calculation when InquiryTemplateEditorToolbar is removed
  top: TOOLBAR_HEIGHT + 16,
  bottom: MINIMUM_BOTTOM_PANEL_HEIGHT + 16,
  left: LEFT_NAV_WIDTH + 16,
};

const RIGHT_PANEL_PORTAL_ID = 'field-schema-editor-right-column-portal';

const InquiryTemplateTreeNavigation = lazy(
  () =>
    import(
      'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/InquiryTemplateTreeNavigation'
    ),
);

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
`;

const ToolbarContainer = styled.div`
  position: absolute;
  width: 100%;
  height: ${TOOLBAR_HEIGHT}px;
  z-index: ${Z_INDICES.floatingBar};
  top: 0;
  left: 0;
`;

const ControlPanelContainer = styled.div`
  position: absolute;
  top: ${TOOLBAR_HEIGHT}px;
  right: 0;
  background-color: ${({ theme }) => theme.color.background.primary};
  height: calc(100% - ${TOOLBAR_HEIGHT}px);
  width: 400px;
  flex: none;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: ${Z_INDICES.withShadow};
`;

const NavPanelContainer = styled.div<{ visible: boolean }>`
  position: absolute;
  width: ${NAV_PANEL_WIDTH}px;
  height: calc(100% - ${TOOLBAR_HEIGHT}px);
  overflow: hidden;
  top: ${TOOLBAR_HEIGHT}px;
  left: 0;
  z-index: ${Z_INDICES.withShadow};
  transition: transform ${TOOLBAR_TRANSITION};
  transform: ${({ visible }) => `translateX(${visible ? '0px' : '-100%'})`};
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const VersionsSectionContainer = styled.div`
  padding-top: ${TOOLBAR_HEIGHT}px;
  display: flex;
  width: 400px;
`;

export interface InquiryTemplateFlowEditorProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  partialInquiryTemplateVersions: PartialInquiryTemplateVersion[];
  isLoadingPartialInquiryTemplateVersions: boolean;
  basePath: string;
  stepsPreviewMap: StepPreviewMap;
  previewPageMap: PreviewPageMap;
  currentLocale: InquiryIso6391LanguageCode;
  extractedWorkflowSteps: LinkedWorkflowStep[];
  moduleOptions: InquiryTemplateModuleOption[];
  modulePreviewMap: ModulePreviewMap;
  nodeTokenObjectToCenter: { token: string } | null;
  workflowVersionMap: WorkflowVersionMap;
  workflowValidationErrors: Record<string, WorkflowConfigError[]>;
  vtmplVersionMap: VerificationTemplateVersionMap;
  isShowingInquiryTemplateVersions: boolean;
  isFormPristine: boolean;
  initialFlowViewTransformationMatrix: TransformationMatrix2D;
  handleAddLocalDraftVersion: () => void;
  handleDiscardDraft: () => Promise<void>;
  handleLoadModule: (
    moduleKey: ModuleOptionKey,
    currentFieldSchema: FieldSchema[],
  ) => Promise<InquiryTemplateModule>;
  handleLoadModulePreview: (moduleKey: ModuleOptionKey) => Promise<void>;
  handleSelectVersion: (inquiryTemplateVersion: string) => void;
  handleSetLocale: (locale: InquiryIso6391LanguageCode) => void;
  handleUpdateFormValues: ({
    steps,
    stepsPreview,
    workflowVersionMap,
    vtmplVersionUpdates,
    fieldSchema,
  }: FlowTreeFormValues) => void;
  hideInquiryTemplateVersions: () => void;
  onFlowViewTransformationMatrixChange: (transformationMatrix: TransformationMatrix2D) => void;
  setNodeTokenObjectToCenter: (tokenObject: { token: string }) => void;
  showShortcutsModal?: () => void;
  bottomPanel: ReactNode;
}

const InquiryTemplateFlowEditor = ({
  inquiryTemplateVersion,
  partialInquiryTemplateVersions,
  isLoadingPartialInquiryTemplateVersions,
  basePath,
  stepsPreviewMap,
  previewPageMap,
  currentLocale,
  extractedWorkflowSteps,
  moduleOptions,
  modulePreviewMap,
  nodeTokenObjectToCenter,
  workflowVersionMap,
  workflowValidationErrors,
  vtmplVersionMap,
  initialFlowViewTransformationMatrix,
  isShowingInquiryTemplateVersions,
  isFormPristine,
  handleAddLocalDraftVersion,
  handleDiscardDraft,
  handleLoadModule,
  handleLoadModulePreview,
  handleSelectVersion,
  handleSetLocale,
  handleUpdateFormValues,
  hideInquiryTemplateVersions,
  onFlowViewTransformationMatrixChange,
  setNodeTokenObjectToCenter,
  showShortcutsModal,
  bottomPanel,
}: InquiryTemplateFlowEditorProps) => {
  const enableLeftNav = useFeatureFlag('dashboard:fle-redesign-left-nav-and-panels');

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isITEInternal = useIsITEInternal();

  const { 'field-schema': fieldSchema, steps, theme } = inquiryTemplateVersion.attributes;
  const { nodeToken, routeIndex } = useParams<{
    nodeToken?: string;
    routeIndex?: string;
  }>();

  const stepsPreview = useMemo(() => Object.values(stepsPreviewMap), [stepsPreviewMap]);
  const pages = useMemo(() => Object.values(previewPageMap), [previewPageMap]);
  // we use JSON.stringify here to properly compare the updated object arrays
  const comparedStepsPreview = useMemo(() => JSON.stringify(stepsPreview), [stepsPreview]);
  const comparedWorkflowSteps = JSON.stringify(extractedWorkflowSteps);
  const [stepsTree, tokensToSteps, _] = useMemo<
    [CombinedStepForTree[], Record<string, TreeNode>, Set<string>]
  >(
    () =>
      buildScreenTreeAndGraph({
        steps,
        stepsPreviewMap,
        previewPageMap,
        extractedWorkflowSteps,
        isITEInternal, // hides verify with persona branches, not needed in dependencies since toggling ITE internal switch causes reload anyways
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages, comparedStepsPreview, comparedWorkflowSteps],
  );

  const editableUngated = useIsEditableWithOptions();
  const editableForFreeTier = useIsEditableWithOptions({
    accessTier: EditorAccessTier.InquiriesFree,
  });
  const editableForBasicTier = useIsEditableWithOptions({
    accessTier: EditorAccessTier.InquiriesBasic,
  });

  // handle centering behavior on deep-link
  useEffect(() => {
    if (nodeToken) {
      // check against root in case of branch node token,
      // which has format '[wfv_id].[step_token].[branchIndex]
      // the root token is also set as the active token to properly highlight
      const rootNodeToken = getRootNodeToken(nodeToken);
      if (tokensToSteps[rootNodeToken] != null) {
        setActiveNodeToken(rootNodeToken);
        setNodeTokenObjectToCenter({ token: nodeToken });
      }
    }
    return () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setNodeTokenObjectToCenter(null);
    };
  }, []);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [activeNodeToken, setActiveNodeToken] = useState<string>(nodeTokenObjectToCenter?.token);
  const [conditionalRouteToEdit, setConditionalRouteToEdit] = useState<number>(Number(routeIndex));
  const [moduleAddStepConfig, setModuleAddStepConfig] = useState<AddStepConfig | null>(null);
  const [stepClipboardData, setStepClipboardData] = useState<NewStep | null>(null);

  // select the node that we wish to center
  useEffect(() => {
    if (nodeTokenObjectToCenter?.token) {
      setActiveNodeToken(getRootNodeToken(nodeTokenObjectToCenter.token));
    }
  }, [nodeTokenObjectToCenter]);

  /**
   * Handles routing to screen editor from a screen node
   * double click or hover controls edit button click with
   * that node set as the current step.
   *
   * The page key is only sent by a combined step node as they have
   * multiple preview pages which map to the same step.
   */
  const handleNavToScreen = useCallback(
    (stepName: string | undefined, pageKey?: string) => {
      let lastPath = stepName ? `/${stepName}` : '';
      if (pageKey != null) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const screenName = deriveCombinedStepScreenName(pageKey, stepName);
        lastPath = `/${stepName}/${screenName}`;
      }
      navigate(`${pathname.replace(/flow.*/, 'screens')}${lastPath}`);
    },
    [navigate, pathname],
  );

  const handleNavToWorkflow = useCallback(
    (workflowVersionId: string, workflowStepToken: string, routeIndex?: number) => {
      const workflowPath = routeIndex
        ? `actions/${workflowVersionId}/${workflowStepToken}/${routeIndex}`
        : `actions/${workflowVersionId}/${workflowStepToken}`;
      navigate(pathname.replace(/flow.*/, workflowPath));
    },
    [navigate, pathname],
  );

  const activeNode = useMemo<TreeNode | undefined>(() => {
    if (activeNodeToken == null) {
      return tokensToSteps[steps[0].name];
    }

    if (tokensToSteps[activeNodeToken] != null) {
      return tokensToSteps[activeNodeToken];
    }
  }, [activeNodeToken, steps, tokensToSteps]);

  const [isInsertingModule, setIsInsertingModule] = useState(false);
  const [showAddModuleModal, { enable: openAddModuleModal, disable: closeAddModuleModal }] =
    useToggle();
  // Don't initially show the tree nav panel if the dashboard:fle-redesign-left-nav-and-panels flag
  // is enabled since it conflicts with the redesign left panel
  const [showLeftNavPanel, { toggle: toggleLeftNavPanel }] = useToggle(!enableLeftNav);

  const handleOpenModuleModal = useCallback(
    (addStepConfig: AddStepConfig) => {
      setModuleAddStepConfig(addStepConfig);
      openAddModuleModal();
    },
    [openAddModuleModal],
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const closePanel = () => setActiveNodeToken(null);

  const handleInsertScreenStep = useCallback(
    ({ addStepConfig, newStep }: { addStepConfig: AddStepConfig; newStep?: NewStep }) => {
      const { updatedSteps, updatedStepsPreview, updatedWorkflowVersion } =
        stepsWorkflowsPreviewsWithInserted({
          flowTreeData: {
            steps,
            stepsPreview,
            workflowVersionMap,
          },
          addStepConfig,
          newStep,
        });
      handleUpdateFormValues({
        steps: updatedSteps,
        stepsPreview: updatedStepsPreview,
        updatedWorkflowVersion,
      });
    },
    [steps, stepsPreview, workflowVersionMap, handleUpdateFormValues],
  );

  const handleInsertModule = useCallback(
    async (key: ModuleOptionKey, moduleAddStepConfig: AddStepConfig) => {
      setIsInsertingModule(true);
      const newModule = await handleLoadModule(key, fieldSchema);
      closeAddModuleModal();
      setIsInsertingModule(false);
      if (newModule && moduleAddStepConfig) {
        const {
          'field-schemas': moduleFieldSchema,
          'verification-template-versions': moduleVtmplvs,
          screens: moduleScreens,
        } = newModule.attributes;
        if (moduleScreens) {
          const updatedFieldSchema = moduleFieldSchema.length
            ? getDedupedFieldSchema(fieldSchema, moduleFieldSchema)
            : null;
          let vtmplVersionUpdates = null;
          if (moduleVtmplvs.length > 0) {
            vtmplVersionUpdates = {
              versionMap: vtmplVersionMapWithInserted(moduleVtmplvs, vtmplVersionMap),
              verificationTemplates: moduleVtmplvs.map((moduleVtmplv) =>
                createMockVerificationTemplateForType(
                  VTMPLV_TEMPLATE_TYPE_MAP[moduleVtmplv.type],
                  undefined,
                  {
                    'latest-draft-version': {
                      data: {
                        id: moduleVtmplv.id,
                        type: ApiModel.VerificationTemplateVersion,
                      },
                    },
                  },
                ),
              ),
            };
          }
          const { updatedSteps, updatedStepsPreview, updatedWorkflowMap } =
            stepsWorkflowsWithInsertedModule(
              {
                steps,
                stepsPreview,
                workflowVersionMap,
              },
              getNewModuleConfig(newModule.attributes, moduleAddStepConfig),
              moduleAddStepConfig,
            );
          handleUpdateFormValues({
            steps: updatedSteps,
            stepsPreview: updatedStepsPreview,
            workflowVersionMap: updatedWorkflowMap,
            fieldSchema: updatedFieldSchema,
            vtmplVersionUpdates,
          });
        }
      }
    },
    [
      fieldSchema,
      steps,
      stepsPreview,
      vtmplVersionMap,
      workflowVersionMap,
      closeAddModuleModal,
      handleUpdateFormValues,
      handleLoadModule,
    ],
  );

  const handleInsertActionStep = useCallback(
    (addStepConfig: AddStepConfig) => {
      const { updatedSteps, updatedStepsPreview, updatedWorkflowVersion } =
        stepsWorkflowsWithInsertedActionNode(
          {
            steps,
            stepsPreview,
            workflowVersionMap,
          },
          addStepConfig,
        );
      handleUpdateFormValues({
        steps: updatedSteps,
        stepsPreview: updatedStepsPreview,
        updatedWorkflowVersion,
      });
    },
    [steps, stepsPreview, workflowVersionMap, handleUpdateFormValues],
  );

  const handleInsertBranchingStep = useCallback(
    (addStepConfig: AddStepConfig) => {
      const { updatedSteps, updatedStepsPreview, updatedWorkflowVersion } =
        stepsWorkflowsWithInsertedBranchingNode(
          {
            steps,
            stepsPreview,
            workflowVersionMap,
          },
          addStepConfig,
        );
      handleUpdateFormValues({
        steps: updatedSteps,
        stepsPreview: updatedStepsPreview,
        updatedWorkflowVersion,
      });
    },
    [steps, stepsPreview, workflowVersionMap, handleUpdateFormValues],
  );

  const handleInsertConnectorStep = useCallback(
    (addStepConfig: AddStepConfig) => {
      const { updatedSteps, updatedStepsPreview, updatedWorkflowVersion } =
        stepsWorkflowsWithInsertedConnectorNode(
          {
            steps,
            stepsPreview,
            workflowVersionMap,
          },
          addStepConfig,
        );
      handleUpdateFormValues({
        steps: updatedSteps,
        stepsPreview: updatedStepsPreview,
        updatedWorkflowVersion,
      });
    },
    [steps, stepsPreview, workflowVersionMap, handleUpdateFormValues],
  );

  const handlePasteStep = useCallback(
    (addStepConfig: AddStepConfig) => {
      if (stepClipboardData != null) {
        handleInsertScreenStep({
          addStepConfig,
          newStep: {
            ...stepClipboardData,
            presentInTree: findStepInTree(stepsTree, stepClipboardData.stepConfig.name) != null,
          },
        });
      }
    },
    [handleInsertScreenStep, stepClipboardData, stepsTree],
  );

  const handleStepTypeSelected = useCallback(
    (stepType: ScreenFlowStepType, addStepConfig: AddStepConfig) => {
      switch (stepType) {
        case ScreenFlowStepType.Integration:
        case ScreenFlowStepType.Screen:
          handleInsertModule(ModuleOptionKey.Screen, addStepConfig);
          break;
        case ScreenFlowStepType.Module:
          handleOpenModuleModal(addStepConfig);
          break;
        case ScreenFlowStepType.Actions:
          handleInsertActionStep(addStepConfig);
          break;
        case ScreenFlowStepType.Branching:
          handleInsertBranchingStep(addStepConfig);
          break;
        case ScreenFlowStepType.Connector:
          handleInsertConnectorStep(addStepConfig);
          break;
        case ScreenFlowStepType.PastedScreen:
          handlePasteStep(addStepConfig);
          break;
        default:
          assertNever(stepType);
      }
    },
    [
      handleInsertScreenStep,
      handleOpenModuleModal,
      handleInsertActionStep,
      handleInsertBranchingStep,
      handleInsertConnectorStep,
      handlePasteStep,
    ],
  );

  const handleCopyStep = (copiedTreeStep: TemplateStepPreview) => {
    if (isScreenStep(copiedTreeStep)) {
      setStepClipboardData(copiedStepSanitized(copiedTreeStep));
      const copiedStepName = getDisplayNameForStep(copiedTreeStep);
      dispatch(showMessage(`${copiedStepName} step copied to clipboard.`, MessageType.Success));
    }
  };

  const handleDeleteScreenStep = useCallback(
    (deleteStepConfig: DeleteStepConfig) => {
      const { updatedSteps, updatedStepsPreview, updatedWorkflowVersion } =
        stepsWorkflowsPreviewsWithDeletedScreenNode({
          flowTreeData: {
            steps,
            stepsPreview,
            workflowVersionMap,
          },
          deleteStepConfig,
        });
      const updatedWorkflowVersionMap = getWorkflowVersionMapUpdated(workflowVersionMap, [
        updatedWorkflowVersion,
      ]);
      // post-processing for a deleted screen step occurs even if updatedSteps is null
      // because a deletion can also update only a workflow version, which may be the
      // last tree reference to a given screen step
      const stepsWithDeleteHandled = stepsWithDeletedScreenHandled(
        deleteStepConfig.currentStep.token,
        updatedSteps ?? steps,
        updatedWorkflowVersionMap,
      );
      const { connectedWorkflowVersionMap, connectedVtmplvMap } = getConnectedSchemaMaps(
        updatedWorkflowVersionMap,
        vtmplVersionMap,
        stepsWithDeleteHandled,
      );
      handleUpdateFormValues({
        steps: stepsWithDeleteHandled,
        stepsPreview: updatedStepsPreview,
        workflowVersionMap: connectedWorkflowVersionMap,
        vtmplVersionUpdates: {
          versionMap: connectedVtmplvMap,
        },
      });
    },
    [steps, stepsPreview, vtmplVersionMap, workflowVersionMap, handleUpdateFormValues],
  );

  const handleDeleteWorkflowStep = useCallback(
    (deleteStepConfig: DeleteStepConfig) => {
      const stepsWorkflowsPreviewsWithDeletedWorkflow =
        stepsWorkflowsPreviewsWithDeletedWorkflowNode(
          steps,
          stepsPreview,
          workflowVersionMap,
          deleteStepConfig,
        );
      let { updatedSteps } = stepsWorkflowsPreviewsWithDeletedWorkflow;
      const { updatedStepsPreview, updatedWorkflowVersion } =
        stepsWorkflowsPreviewsWithDeletedWorkflow;

      updatedSteps = updatedSteps ?? steps;
      // Build the graph so that we know which screens and workflows were
      // disconnected due to the deletion of the workflow step
      const visitedSet = buildConnectedSteps({
        steps: updatedSteps,
        stepsPreviewMap: keyBy('name', updatedStepsPreview),
        previewPageMap,
        extractedWorkflowSteps,
        isITEInternal,
      });

      updatedSteps = updatedSteps.filter((step) => visitedSet.has(step.name));
      const { connectedWorkflowVersionMap, connectedVtmplvMap } = getConnectedSchemaMaps(
        getWorkflowVersionMapUpdated(workflowVersionMap, [updatedWorkflowVersion]),
        vtmplVersionMap,
        updatedSteps,
      );

      handleUpdateFormValues({
        steps: updatedSteps,
        stepsPreview: updatedStepsPreview,
        workflowVersionMap: connectedWorkflowVersionMap,
        vtmplVersionUpdates: {
          versionMap: connectedVtmplvMap,
        },
      });
    },
    [
      steps,
      stepsPreview,
      vtmplVersionMap,
      workflowVersionMap,
      handleUpdateFormValues,
      extractedWorkflowSteps,
      isITEInternal,
      previewPageMap,
    ],
  );

  const handleDeleteStep = useCallback(
    (deleteStepConfig: DeleteStepConfig) => {
      // deselect active node if its being deleted
      if (deleteStepConfig.currentStep.token === activeNodeToken) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setActiveNodeToken(null);
      }

      if (deleteStepConfig.currentStep.type === 'workflow') {
        handleDeleteWorkflowStep(deleteStepConfig);
      } else {
        handleDeleteScreenStep(deleteStepConfig);
      }
    },
    [activeNodeToken, handleDeleteScreenStep, handleDeleteWorkflowStep],
  );

  const controlPanelContent = useMemo<React.ReactNode>(() => {
    if (activeNode == null) {
      return null;
    }

    const stepIndex = steps.findIndex((s) => s.name === activeNode.step.token);
    switch (activeNode.step.type) {
      case UiStepType.Complete:
        return (
          <CompleteStepPanel
            step={activeNode.step}
            stepIndex={stepIndex}
            editable={editableUngated}
            handleClose={closePanel}
          />
        );
      case UiStepType.Document:
      case UiStepType.Integration:
      case UiStepType.VerifyWithPersona:
      case UiStepType.Selfie:
      case UiStepType.SelfieReflection:
      case UiStepType.GovernmentId:
      case UiStepType.Ui:
      case UiStepType.Loop:
        return null;
      case 'workflow': {
        const currentStep = activeNode.step as LinkedWorkflowStep;
        const workflowStep = getConditionalStepForToken(
          currentStep.workflowStep.token,
          workflowVersionMap[activeNode.step.workflowVersionId],
        );
        return workflowStep?.type === WorkflowStepType.Conditional ? (
          <WorkflowStepPanel
            step={workflowStep}
            basePath={basePath}
            editable={editableUngated}
            errors={
              workflowValidationErrors[activeNode.step.workflowVersionId]?.filter(
                (error) => error.token === workflowStep.token,
              ) ?? []
            }
            conditionalRouteToEdit={conditionalRouteToEdit}
            setConditionalRouteToEdit={setConditionalRouteToEdit}
            workflowVersion={workflowVersionMap[activeNode.step.workflowVersionId]}
            handleClose={closePanel}
          />
        ) : null;
      }
      default:
        assertNever(activeNode.step);
    }
  }, [
    activeNode,
    basePath,
    conditionalRouteToEdit,
    editableUngated,
    steps,
    workflowValidationErrors,
    workflowVersionMap,
  ]);

  const rightColumnRef = useRef<HTMLDivElement>(null);

  const fieldReferenceMap = useMemo<FieldReferenceMap>(
    () =>
      buildFieldReferenceMap({
        fieldSchema: fieldSchema,
        steps: steps,
        workflowVersionMap: workflowVersionMap,
        vtmplVersionMap: vtmplVersionMap,
      }),
    [fieldSchema, steps, workflowVersionMap, vtmplVersionMap],
  );
  // This needs to be wrapped in useCallback because it is a useCallback dependency in FieldSchemaEditorCardV2
  const handleUpdateFieldSchema = useCallback(
    (fieldSchema) => handleUpdateFormValues({ fieldSchema }),
    [],
  );

  const navPanels: NavItem[][] = useMemo(() => {
    return [
      [
        {
          icon: <ListIcon />,
          label: 'Steps',
          path: {
            to: `${basePath}/milestone1/steps`,
            basePath,
          },
          type: { size: 'small' },
          panelContent: (
            <Suspense fallback={<TreeNavigationNullState />}>
              <InquiryTemplateTreeNavigation
                steps={steps}
                activeNodeToken={activeNodeToken}
                nodeTokenObjectToCenter={nodeTokenObjectToCenter}
                workflowVersionMap={workflowVersionMap}
                editable={editableUngated}
              />
            </Suspense>
          ),
        },
      ],
      [
        {
          icon: <DatabaseIcon />,
          label: 'Fields',
          path: {
            to: `${basePath}/milestone1/fields`,
            basePath,
          },
          type: { size: 'large', rightColumnId: RIGHT_PANEL_PORTAL_ID },
          panelContent: (
            <FieldSchemaEditorCardV2
              fieldSchema={fieldSchema}
              fieldReferenceMap={fieldReferenceMap}
              versionId={inquiryTemplateVersion.id}
              editable={editableUngated}
              handleUpdateFieldSchema={handleUpdateFieldSchema}
              rightPanelPortalId={RIGHT_PANEL_PORTAL_ID}
              // unused
              title=''
              rightColumnRef={rightColumnRef}
              currentFieldKey={undefined}
            />
          ),
        },
        {
          icon: <CircleCheckIcon />,
          label: 'Verifications',
          path: {
            to: `${basePath}/milestone1/verifications`,
            basePath,
          },
          type: { size: 'large' },
          panelContent: <></>,
        },
        {
          icon: <SettingsIcon />,
          label: 'Settings',
          path: {
            to: `${basePath}/milestone1/settings`,
            basePath,
          },
          type: { size: 'large' },
          panelContent: (
            <FormSectionGeneralConfig
              inquiryTemplateVersion={inquiryTemplateVersion}
              // TODO (fawn): make sure links to fields work once the fields panel is added
              //  (specifically for this settings panel, check the link to fields in the country config)
              basePath={basePath}
              currentLocale={currentLocale}
              steps={steps}
              stepsPreviewMap={stepsPreviewMap}
              previewPageMap={previewPageMap}
              extractedWorkflowSteps={extractedWorkflowSteps}
              vtmplVersionMap={vtmplVersionMap}
              workflowVersionMap={workflowVersionMap}
              handleSetLocale={handleSetLocale}
              handleLoadModule={handleLoadModule}
              handleUpdateFormValues={handleUpdateFormValues}
              fieldSchema={fieldSchema}
              // unused
              title=''
              rightColumnRef={rightColumnRef}
            />
          ),
        },
      ],
    ];
  }, [
    activeNodeToken,
    basePath,
    currentLocale,
    editableUngated,
    extractedWorkflowSteps,
    fieldReferenceMap,
    fieldSchema,
    handleLoadModule,
    handleSetLocale,
    handleUpdateFieldSchema,
    handleUpdateFormValues,
    inquiryTemplateVersion,
    nodeTokenObjectToCenter,
    previewPageMap,
    steps,
    stepsPreviewMap,
    vtmplVersionMap,
    workflowVersionMap,
  ]);

  const InquiryFlowEditorStore = useInquiryTemplateTreeStore({
    setActiveNodeToken,
    tree: stepsTree,
  });

  return (
    <InquiryTemplateTreeProvider value={InquiryFlowEditorStore}>
      <RootContainer>
        <ContentContainer>
          <PanZoomBox
            showZoomControls={false}
            initialTransformationMatrix={initialFlowViewTransformationMatrix}
            overlayContent={
              enableLeftNav ? (
                <>
                  <NavAndPanelsOverlay items={navPanels} bottomPanel={bottomPanel} />
                  <ToolbarContainer>
                    <InquiryTemplateEditorToolbar
                      leftNavPanel={{
                        sidePanelVisible: showLeftNavPanel,
                        setSidePanelVisible: toggleLeftNavPanel,
                      }}
                      showShortcutsModal={showShortcutsModal}
                    />
                  </ToolbarContainer>
                </>
              ) : (
                <>
                  <ToolbarContainer>
                    <InquiryTemplateEditorToolbar
                      leftNavPanel={{
                        sidePanelVisible: showLeftNavPanel,
                        setSidePanelVisible: toggleLeftNavPanel,
                      }}
                      showShortcutsModal={showShortcutsModal}
                    />
                  </ToolbarContainer>
                  <NavPanelContainer visible={showLeftNavPanel}>
                    <Suspense fallback={<TreeNavigationNullState />}>
                      <InquiryTemplateTreeNavigation
                        steps={steps}
                        activeNodeToken={activeNodeToken}
                        nodeTokenObjectToCenter={nodeTokenObjectToCenter}
                        workflowVersionMap={workflowVersionMap}
                        editable={editableUngated}
                      />
                    </Suspense>
                  </NavPanelContainer>
                </>
              )
            }
            onTransformationMatrixChange={onFlowViewTransformationMatrixChange}
            zoomToFitPadding={enableLeftNav ? FLE3_ZOOM_TO_FIT_PADDING : FLE2_ZOOM_TO_FIT_PADDING}
            withDynamicUpdates={activeNode != null}
            withExternalZoomControls
            useKeydownShortcuts
            zoomToFit
          >
            <InquiryTemplateTree
              basePath={removeLastPath(basePath)}
              steps={steps}
              tree={stepsTree}
              theme={theme}
              workflowValidationConfig={{
                versions: workflowVersionMap,
                errors: workflowValidationErrors,
              }}
              activeNodeToken={activeNodeToken}
              clipboardHasStep={stepClipboardData != null}
              editableForBasicTier={editableForBasicTier}
              editableForFreeTier={editableForFreeTier}
              handleCopyStep={handleCopyStep}
              handleDeleteStep={handleDeleteStep}
              handleNavToScreen={handleNavToScreen}
              handleNavToWorkflow={handleNavToWorkflow}
              handleStepTypeSelected={handleStepTypeSelected}
              selectConditionalRouteToEdit={setConditionalRouteToEdit}
              selectNode={setActiveNodeToken}
            />
          </PanZoomBox>
          {activeNodeToken && controlPanelContent && (
            <>
              {/* required portal to this level to prevent conditional panel cutoff in safari */}
              <div id={EDIT_CONDITIONAL_PANEL_ID} />
              <ControlPanelContainer>{controlPanelContent}</ControlPanelContainer>
            </>
          )}
          {isShowingInquiryTemplateVersions && (
            <VersionsSectionContainer>
              <VersionsSection
                versions={inquiryTemplateVersionMetadata(partialInquiryTemplateVersions)}
                isLoading={isLoadingPartialInquiryTemplateVersions}
                hide={hideInquiryTemplateVersions}
                currentVersionId={inquiryTemplateVersion.id}
                isFormPristine={isFormPristine}
                formName={INQUIRY_TEMPLATE_EDITOR_FORM_NAME}
                handleSelectVersion={handleSelectVersion}
                handleAddLocalDraftVersion={handleAddLocalDraftVersion}
                handleDiscardDraft={handleDiscardDraft}
              />
            </VersionsSectionContainer>
          )}
          <ModalWrapper
            shouldCloseOnEsc={true}
            isOpen={showAddModuleModal}
            handleCloseModal={closeAddModuleModal}
          >
            <ScreenFlowAddModuleModal
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              addStepConfig={moduleAddStepConfig}
              moduleOptions={moduleOptions}
              modulePreviewMap={modulePreviewMap}
              handleLoadModulePreview={handleLoadModulePreview}
              handleInsertModule={handleInsertModule}
              isInsertingModule={isInsertingModule}
              handleCloseModal={closeAddModuleModal}
              inquiryTemplateTheme={theme}
            />
          </ModalWrapper>
        </ContentContainer>
      </RootContainer>
    </InquiryTemplateTreeProvider>
  );
};

export default InquiryTemplateFlowEditor;
